/**
 * Checklist state management hook
 *
 * Preact hook for managing checklist state with localStorage persistence.
 * Initializes from localStorage on mount and saves on state changes.
 */

import { useState, useEffect, useCallback, useRef } from 'preact/compat'
import {
  getChecklistState,
  setChecklistState,
  createEmptyState,
  clearExpiredChecklists,
} from './storage'

/**
 * Hook for managing checklist state with localStorage persistence
 * @param {number|string} creationId - The Create card ID
 * @return {object} - { state, toggleIngredient, toggleStep, toggleGroup, isLoaded }
 */
export const useChecklistState = (creationId) => {
  const [state, setState] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const isInitialMount = useRef(true)

  // Initialize state from localStorage on mount
  useEffect(() => {
    // Clean up expired checklists on first load
    clearExpiredChecklists()

    // Try to load existing state from localStorage
    const savedState = getChecklistState(creationId)

    if (savedState) {
      setState(savedState)
    } else {
      setState(createEmptyState())
    }

    setIsLoaded(true)
  }, [creationId])

  // Save state to localStorage when it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (state && isLoaded) {
      setChecklistState(creationId, state)
    }
  }, [state, creationId, isLoaded])

  /**
   * Toggle an ingredient's checked state
   * @param {string} ingredientKey - Key in format "group_{groupIndex}_item_{itemIndex}"
   */
  const toggleIngredient = useCallback((ingredientKey) => {
    setState((prevState) => {
      if (!prevState) return prevState

      const currentValue = prevState.ingredients[ingredientKey] || false

      return {
        ...prevState,
        ingredients: {
          ...prevState.ingredients,
          [ingredientKey]: !currentValue,
        },
      }
    })
  }, [])

  /**
   * Toggle a step's checked state
   * @param {string} stepKey - Key in format "step_{stepIndex}"
   */
  const toggleStep = useCallback((stepKey) => {
    setState((prevState) => {
      if (!prevState) return prevState

      const currentValue = prevState.instructions[stepKey] || false

      return {
        ...prevState,
        instructions: {
          ...prevState.instructions,
          [stepKey]: !currentValue,
        },
      }
    })
  }, [])

  /**
   * Toggle a group's collapsed state
   * @param {string} groupKey - Key for the group (e.g., "group_0", "group_1")
   */
  const toggleGroup = useCallback((groupKey) => {
    setState((prevState) => {
      if (!prevState) return prevState

      const collapsedGroups = prevState.collapsedGroups || []
      const isCollapsed = collapsedGroups.includes(groupKey)

      return {
        ...prevState,
        collapsedGroups: isCollapsed
          ? collapsedGroups.filter((key) => key !== groupKey)
          : [...collapsedGroups, groupKey],
      }
    })
  }, [])

  /**
   * Check if an ingredient is checked
   * @param {string} ingredientKey - Key in format "group_{groupIndex}_item_{itemIndex}"
   * @return {boolean}
   */
  const isIngredientChecked = useCallback(
    (ingredientKey) => {
      if (!state || !state.ingredients) return false
      return !!state.ingredients[ingredientKey]
    },
    [state]
  )

  /**
   * Check if a step is checked
   * @param {string} stepKey - Key in format "step_{stepIndex}"
   * @return {boolean}
   */
  const isStepChecked = useCallback(
    (stepKey) => {
      if (!state || !state.instructions) return false
      return !!state.instructions[stepKey]
    },
    [state]
  )

  /**
   * Check if a group is collapsed
   * @param {string} groupKey - Key for the group
   * @return {boolean}
   */
  const isGroupCollapsed = useCallback(
    (groupKey) => {
      if (!state || !state.collapsedGroups) return false
      return state.collapsedGroups.includes(groupKey)
    },
    [state]
  )

  /**
   * Reset all checklist state to empty
   */
  const resetChecklist = useCallback(() => {
    setState(createEmptyState())
  }, [])

  /**
   * Get count of checked ingredients
   * @return {number}
   */
  const getCheckedIngredientsCount = useCallback(() => {
    if (!state || !state.ingredients) return 0
    return Object.values(state.ingredients).filter(Boolean).length
  }, [state])

  /**
   * Get count of checked steps
   * @return {number}
   */
  const getCheckedStepsCount = useCallback(() => {
    if (!state || !state.instructions) return 0
    return Object.values(state.instructions).filter(Boolean).length
  }, [state])

  return {
    state,
    isLoaded,
    toggleIngredient,
    toggleStep,
    toggleGroup,
    isIngredientChecked,
    isStepChecked,
    isGroupCollapsed,
    resetChecklist,
    getCheckedIngredientsCount,
    getCheckedStepsCount,
  }
}

export default useChecklistState
